import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { IoPersonOutline } from "react-icons/io5";
import "./accountTypeSelect.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
  "User-Timezone": getUserTimeZone(),
});

export default function AccountTypeSelect() {
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken")
    );
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/sign-in", { replace: true });
    }
  }, [token, navigate]);

  const handleNext = async () => {
    if (!selected) {
      toast.error("Please select account type");
      return;
    }

    setLoading(true);

    try {
      const chosenStatus = selected === "business" ? "salon" : "individual";

      const tokenNow =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!tokenNow) {
        toast.error("No access token found. Please sign in again.");
        return;
      }

      const res = await fetch(`${BASE_URL}/auth/update-status/`, {
        method: "POST",
        headers: buildHeaders(tokenNow),
        body: JSON.stringify({ status: chosenStatus }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.detail || "Failed to update account type on server.");
        return;
      }

      localStorage.setItem("user_status", chosenStatus);
      localStorage.setItem("user_timezone", getUserTimeZone());

      const forcedRoute =
        chosenStatus === "salon" ? "/biography-salon" : "/biography-individual";

      sessionStorage.setItem("onboarding_force_route", forcedRoute);
      sessionStorage.setItem("onboarding_force_status", chosenStatus);

      toast.success("Account type saved!");
      navigate(forcedRoute, { replace: true });
    } catch (err) {
      console.error("UPDATE STATUS ERROR:", err);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === "Enter" && selected && !loading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [selected, loading]);

  const options = [
    {
      id: "individual",
      icon: <IoPersonOutline />,
      title: "Individual",
      desc: "Designed for freelancers or solo professionals working independently.",
    },
    {
      id: "business",
      icon: <HiOutlineBuildingOffice2 />,
      title: "Small or Medium Business",
      desc: "Best for teams — manage staff, services, and client bookings in one place.",
    },
  ];

  return (
    <motion.div
      className="account-type"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="account-type-main">
        <motion.div
          className="account-type-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex flex-col gap-4 mx-auto text-center">
            <h2 className="account-type-title">
              What kind of account would you like to open?
            </h2>

            <p className="account-type-subtitle">
              You can add another account later on, too
            </p>
          </div>
          <div className="account-type-options">
            {options.map((opt) => (
              <motion.div
                key={opt.id}
                className={`account-option ${selected === opt.id ? "selected" : ""}`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelected(opt.id)}
              >
                <div
                  className={`option-icon ${selected === opt.id ? "active" : ""}`}
                >
                  {opt.icon}
                </div>
                <h3>{opt.title}</h3>
                <p>{opt.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.button
            className="account-type-btn"
            disabled={!selected || loading}
            onClick={handleNext}
            whileHover={{ scale: selected ? 1.05 : 1 }}
            whileTap={{ scale: selected ? 0.95 : 1 }}
          >
            {loading ? "Saving..." : "Next"}
          </motion.button>
        </motion.div>
      </main>
    </motion.div>
  );
}
